/**
 * Copyright (c) 2024 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useState
} from 'react';
import {Paper} from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Popper from '@material-ui/core/Popper';
import {makeStyles} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import {PageTitle} from '@ion/applications/src/pages/admin/AdminContainer';
import {translate} from '@ion/core/src/locale/locale';
import {normalize} from '@ion/core/src/utils/StringUtil';
import TextField from '@ion/dls/src/components/textfield/TextField';
import {SaveButton} from '@ion/dls/src/editors/controls/SaveButton';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {
  CallbackArgumentsType,
  ColumnWithOrderKey,
  HIDE_DURATION,
  MAX_LENGTH
} from './types';
import useSavedColumnView from './useSavedColumnView';

interface SaveColumnEditorProps {
  getSavedColumnList?: () => void;
  checkLoadingStatus?: (isLoading?: boolean) => void;
  onClose?: () => void;
  isOpen?: boolean;
  anchorEl?: HTMLButtonElement;
  selectedColumns?: ColumnWithOrderKey[] | null;
  pageName?: PageTitle;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  popper : {
    zIndex : dlsZIndex('savedColumnEditorPoper')
  },
  paper : {
    minHeight : '150px',
    width     : '295px',
    padding   : '20px 20px'
  },
  poperTitle : {
    paddingBottom : '16px',
    paddingTop    : '5px',
    alignItems    : 'center',
    textTransform : 'uppercase',
    fontSize      : 16,
    fontFamily    : theme.typography.fontFamily
  },
  inputText : {
    fontSize    : 14,
    fontFamily  : theme.typography.fontFamily,
    paddingLeft : 0
  },
  searchIcon : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  buttonWrapper : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    marginTop      : '15px'
  },
  button : {
    color    : dlsColor('aaBlue'),
    fontSize : 12
  },
  errorText : {
    color      : dlsColor('caRed'),
    marginLeft : '0 !important'
  },
  helperText : {
    paddingLeft : '0px !important'
  },
  closeIcon : {
    cursor  : 'pointer !important',
    opacity : '0.2 !important',
    height  : '20px !important'
  }
}));

/**
 * @SaveColumnEditor
 *
 * The SaveColumnEditor provides a UI to show text field editor dialog,
 * which allows the customer to save a new column view with a specific name.
 *
 * ```typescript
 *
 *
 *     <SaveColumnEditor isOpen={isViewSaveColumnEditor}
 *       anchorEl={saveAsAnchorEl}
 *       selectedColumns={selectedColumns}
 *       onClose={onCloseSaveColumnEditor} />
 * ```
 *
 * @see SavedColumnContainer
 */
export const SaveColumnEditor = ({
  selectedColumns,
  getSavedColumnList,
  checkLoadingStatus,
  onClose,
  anchorEl,
  pageName,
  isOpen = false
}: SaveColumnEditorProps) => {
  const classes                               = useStyles();
  const [isShowSnackbar, setIsShowSnackbar]   = useState(false);
  const [error, setError]                     = useState(false);
  const [savedColumnName, setSavedColumnName] = useState<string>('');
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const {onSave, loading}                     = useSavedColumnView({selectedColumns, pageName});

  const {
    successMessage,
    inputPlaceholder,
    morethanMaxLength,
    nameExist,
    title
  } = translate('common:columnChooser.saveColumnEditor', {
    returnObjects : true,
    columnName    : savedColumnName,
    maxLength     : MAX_LENGTH
  });

  useEffect(() => {
    if (!isOpen) {
      setSavedColumnName('');
      setError(false);
    }
  }, [isOpen]);

  useEffect(() => {
    checkLoadingStatus(loading);
  }, [loading]);

  /**
   * Invoked when the user chooses to close the snackbar message.
   */
  const onCloseMessage = useCallback(function() {
    setIsShowSnackbar(false);
  }, []);

  /**
   * Invoked when the user click on cancel button and chooses to close
   * the saved column editor.
   */
  const handleClose = useCallback(function() {
    setSavedColumnName('');
    onClose();
  }, []);

  /**
   * Invoked when input field changes.
   */
  const onChange = useCallback(function({target: {value}}) {
    setSavedColumnName(value);
    setError(false);
  }, []);

  /**
   * Invoked when the user chooses to clear the input field text.
   */
  const onTextClear = useCallback(function() {
    setSavedColumnName('');
    setError(false);
  }, []);

  /**
   * Invoked when the user clicks the Done button, at which point it check the validation
   * if the current name is already exist or not, if the length of saved column is
   * less than MAX_LENGTH or not, if column saved successfully or not. And on the basis
   * of validation check the snackbar message will update.
   */
  const checkStatusOnSave = useCallback(function({
    isNameExist = false,
    isMorethanMax = false,
    isSuccess = false
  }: CallbackArgumentsType) {
    setError(isNameExist);
    setIsShowSnackbar(isMorethanMax || isSuccess);
    if (isMorethanMax) {
      setSnackbarMessage(morethanMaxLength);
    } else if (isSuccess) {
      getSavedColumnList?.();
      handleClose();
      setSnackbarMessage(successMessage);
    }
  }, []);

  /**
   * Invoked when the user click on Done button, and save the selected column
   * with the given name.
   */
  const onSubmit = useCallback(function() {
    onSave(savedColumnName, checkStatusOnSave);
  }, [savedColumnName]);

  return (
    <>
      <Popper open={isOpen}
        role={'dialog'}
        anchorEl={anchorEl}
        transition
        disablePortal
        placement='bottom'
        className={classes.popper}>
        <Paper classes={{root: classes.paper}}>
          <div className={classes.poperTitle}>
            <span>{title}</span>
          </div>
          <TextField onChange={onChange}
            placeholder={inputPlaceholder}
            value={savedColumnName}
            inputProps={{maxLength: 32}}
            InputProps={{
              classes : {
                input : classes.inputText
              },
              endAdornment : (<div className={classes.searchIcon}>
                {Boolean(savedColumnName.length) && (<CloseIcon onClick={onTextClear}
                  className={classes.closeIcon} />)}
              </div>)
            }}
            FormHelperTextProps={{className: classes.helperText}}
            hasError={error}
            errorText={
              <span className={classes.errorText}>
                {nameExist}
              </span>
            } />
          <div className={classes.buttonWrapper}>
            <Button className={classes.button}
              variant='text'
              onClick={handleClose}>
              {translate('common:editors.shared.cancel')}
            </Button>
            <SaveButton autoWidth
              onSave={onSubmit}
              disabled={!normalize(savedColumnName) || loading}
              loading={loading} />
          </div>
        </Paper>
      </Popper>
      <IonSnackbar show={isShowSnackbar}
        zIndex={dlsZIndex('savedColumnSnackbar')}
        message={snackbarMessage}
        actionLabel={translate('common:editors.delete.ok')}
        onAction={onCloseMessage}
        onClose={onCloseMessage}
        autoHideDuration={HIDE_DURATION} />
    </>
  );
};

export default SaveColumnEditor;
